import { MaterialSummary } from "../../schemas";
import { addDays, format } from "date-fns";
import { DATE_FORMAT, getDateFnsLocale } from "../../date-utils";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useRef } from "react";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const locale = getDateFnsLocale();

export function Summary(props: { from: Date, to: Date, data: MaterialSummary[], colors: { [key: string]: string } }) {

  const dates = getDatesBetween(props.from, props.to);
  const materials = props.data.map(item => ({ id: item.material_id, name: item.material_name }))
  const materialSet = materials?.filter((material, idx) => materials.findIndex(m => m.id === material.id) === idx);

  const graphDataDict = props.data.reduce((dict, item) => {
    if (!(item.date in dict)) {
      dict[item.date] = {}
    }
    dict[item.date][item.material_id] = item.count

    return dict
  }, {} as { [key: string]: { [key: string]: number } })

  const graphData = graphDataDict && dates.map(date => {
    return { date, ...materialSet && Object.fromEntries(materialSet?.map(m => [m.id, (graphDataDict[date] || {})[m.id] || 0])) }
  })

  const ref = useRef(null);
  const chartHeight = (ref.current as any)?.props?.height;

  const nDates = dates.length;

  //reference: https://tyablog.net/2020/03/09/javascript-hash-color/
  const stringToColor = (str: String) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        // RGBに変換
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
}


  return (
    props.data ? (
      <ResponsiveContainer>
        <LineChart ref={ref} data={graphData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            padding={{ left: 30, right: 30 }}
            interval={0}
            tickFormatter={(date, i) => {
              const d = new Date(date);
              const chartWidth = (ref.current as any)?.props?.width;
              const a = chartWidth / nDates;
              const day = d.getDate();

              if (a < 25) {
                if (day % 5 !== 0) return '';
                return day <= 5 || i <= 5 ? format(d, 'M/d') : format(d, 'd')
              }
              if (a < 45) {
                if (day % 2 !== 0) return '';
                return day <= 2 || i <= 1 ? format(d, 'M/d') : format(d, 'd')
              }
              return day === 1 || i === 0 ? format(d, 'M/d') : format(d, 'd')
            }}
          />
          <YAxis />
          <Tooltip
            content={({ payload, label }) => {
              const rows = payload?.filter(row => !!row.value)
                .map((row, i) => (
                  <tr key={i} style={{ color: row.color, padding: 0 }}>
                    <td>{row.name}</td>
                    <td>:</td>
                    <td style={{ textAlign: 'right' }}>{row.value}</td>
                  </tr>
                ));

              return (
                <Box style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', fontSize: 'small', padding: 4, border: '1px solid grey' }}>
                  <div>{label && format(new Date(label), 'PP', { locale })}</div>
                  {
                    rows && rows.length > 0 ? (
                      <table >
                        <tbody>
                          {rows}
                        </tbody>
                      </table>
                    )
                      : <div style={{ fontStyle: 'italic', color: '#808080' }}>(No Data)</div>
                  }
                </Box>
              )
            }}
          />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="top"
            height={chartHeight || 100}
            content={ps => {
              return (
                <Box sx={{ ml: 1, maxWidth: 250, height: '100%', overflowY: 'auto' }}>
                  {
                    ps.payload?.map((p, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1}>
                        <div style={{ backgroundColor: p.color, width: 40, height: 2 }}></div>
                        <Typography
                          title={p.value}
                          sx={{ color: p.color, width: 150, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                        >
                          {p.value}
                        </Typography>
                        <IconButton size="small" href={`chart/${(p as any).dataKey}?from=${format(props.from, DATE_FORMAT)}&to=${format(props.to, DATE_FORMAT)}`}>
                          <ArrowForwardIcon fontSize="inherit" />
                        </IconButton>
                      </Stack>
                    ))
                  }
                </Box>
              )
            }}
          />
          {materialSet && materialSet.map((m, i) =>
            <Line
              key={m.id}
              type="linear"
              dataKey={m.id}
              name={materialSet.filter(n => n.id === m.id)[0].name}
              stroke={stringToColor(m.id)}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    ) : <div>no data</div>
  )
}

export function getDatesBetween(from: Date, to: Date): string[] {
  const dates = [];
  let currDate = from;
  while (currDate <= to) {
    dates.push(format(currDate, DATE_FORMAT));
    currDate = addDays(currDate, 1);
  }
  return dates;
}
